/**
 * Idle lock overlay (#2214, #2217).
 *
 * Sits on top of the current route. Nothing navigates, so unfinished forms
 * stay mounted. Password unlock restores the access token in place. Paya ID
 * unlock opens a same-origin popup; the opener is not remounted. If the
 * browser blocks the popup, this page falls back to a full redirect.
 *
 * Every state has a way forward that is not a bare "sign out" (#2882): a
 * failed `/auth/context` shows a retry (and refetches on focus / reconnect),
 * and an unlock the server can never grant — the refresh family is gone, or
 * the account cannot use this proof — offers a sign-in that returns here.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  Box,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuthContext, useSignOut, useUnlockOidcStart, useUnlockSession } from '@/hooks/useAuth';
import { INACTIVITY_LOGOUT_REASON } from './sessionBroadcast';
import { ApiError } from '@/lib/api';
import { isSafeInternalPath, useAuthStore } from '@/stores/authStore';
import {
  assignUnlockPopupLocation,
  isSafeUnlockStartUrl,
  openUnlockPopup,
  prepareUnlockPopup,
  subscribeUnlockPopup,
  type UnlockPopupIdentity,
} from './unlockPopup';

function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

type CapturedUnlock = UnlockPopupIdentity & { refreshToken: string };

/**
 * Answers after which unlocking cannot work no matter how often it is tried
 * (#2882). A wrong password is a 401 too, but without these codes.
 *
 * `session_expired`: the refresh family is gone (TTL, family cap, logout
 * elsewhere, deactivated user). The message fallback covers a server that
 * predates the code. `oidc_required` / `portal_session_required` /
 * `session_not_locked`: this family cannot be unlocked with this proof.
 */
const UNRECOVERABLE_UNLOCK_CODES = new Set([
  'session_expired',
  'oidc_required',
  'portal_session_required',
  'session_not_locked',
]);
const UNRECOVERABLE_UNLOCK_MESSAGES = new Set([
  'Invalid refresh token',
  'User not found or deactivated',
]);

function isUnrecoverableUnlockError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status !== 401 && error.status !== 403) return false;
  const data = error.data;
  if (typeof data !== 'object' || data === null) return false;
  const { code, message } = data as { code?: unknown; message?: unknown };
  if (typeof code === 'string' && UNRECOVERABLE_UNLOCK_CODES.has(code)) return true;
  return (
    error.status === 401 &&
    typeof message === 'string' &&
    UNRECOVERABLE_UNLOCK_MESSAGES.has(message)
  );
}

function hasLocalRefreshToken(): boolean {
  const { refreshToken } = useAuthStore.getState();
  return refreshToken !== null && refreshToken !== '';
}

function captureUnlockIdentity(): CapturedUnlock | null {
  const state = useAuthStore.getState();
  if (!state.isLocked || state.user === null || state.sessionFamilyId === null) return null;
  if (state.refreshToken === null || state.refreshToken === '') return null;
  return {
    sessionFamilyId: state.sessionFamilyId,
    lockGeneration: state.lockGeneration,
    userId: state.user.id,
    tenantId: state.user.tenantId,
    sessionEpoch: state.sessionEpoch,
    refreshToken: state.refreshToken,
  };
}

function identityStillCurrent(expected: CapturedUnlock): boolean {
  const state = useAuthStore.getState();
  return (
    state.isLocked &&
    state.isAuthenticated &&
    state.user !== null &&
    state.user.id === expected.userId &&
    state.user.tenantId === expected.tenantId &&
    state.sessionFamilyId === expected.sessionFamilyId &&
    state.lockGeneration === expected.lockGeneration &&
    state.sessionEpoch === expected.sessionEpoch &&
    state.refreshToken === expected.refreshToken
  );
}

export function LockScreen() {
  const { t } = useTranslation('common');
  const firstName = useAuthStore((s) => s.user?.firstName?.trim() ?? '');
  const {
    data: authContext,
    isError: contextFailed,
    isFetching: contextFetching,
    refetch: refetchAuthContext,
  } = useAuthContext();
  const payaIdEnabled = authContext?.oidcEnabled === true;
  const contextReady = authContext !== undefined;
  const unlock = useUnlockSession();
  const startOidcUnlock = useUnlockOidcStart();
  const signOut = useSignOut();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [oidcPending, setOidcPending] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const popupCleanupRef = useRef<(() => void) | null>(null);

  // The app-wide query client never refetches on focus, so an overlay that
  // mounted while the network was still waking up would otherwise wait on a
  // failed `/auth/context` forever (#2882).
  useEffect(() => {
    if (contextReady) return;
    const retry = () => {
      if (document.visibilityState === 'hidden') return;
      void refetchAuthContext();
    };
    window.addEventListener('focus', retry);
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', retry);
    return () => {
      window.removeEventListener('focus', retry);
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }, [contextReady, refetchAuthContext]);

  useEffect(
    () => () => {
      popupCleanupRef.current?.();
      popupCleanupRef.current = null;
    },
    [],
  );

  const displayName = firstName;
  const titleId = 'portal-lock-title';
  const descriptionId = 'portal-lock-description';

  const markSessionExpired = () => {
    setPassword('');
    setError(null);
    setSessionExpired(true);
  };

  /**
   * Sign in again and come back to this page. Same continuation as the idle
   * sign-out (#2212): the login page explains why and returns here after.
   */
  const handleSignInAgain = async () => {
    if (signingIn) return;
    setSigningIn(true);
    const returnTo = currentReturnPath();
    await signOut({
      reason: INACTIVITY_LOGOUT_REASON,
      ...(isSafeInternalPath(returnTo) ? { returnTo } : {}),
    });
  };

  const handlePasswordUnlock = async () => {
    setError(null);
    if (!hasLocalRefreshToken()) {
      markSessionExpired();
      return;
    }
    try {
      await unlock.mutateAsync({ password });
      setPassword('');
    } catch (err) {
      if (isUnrecoverableUnlockError(err)) {
        markSessionExpired();
        return;
      }
      setError(t('session.unlockFailed'));
    }
  };

  const failOidcUnlock = (popup: Window | null, cause?: unknown) => {
    popupCleanupRef.current?.();
    popupCleanupRef.current = null;
    try {
      popup?.close();
    } catch {
      // Already gone.
    }
    setOidcPending(false);
    if (isUnrecoverableUnlockError(cause) || !hasLocalRefreshToken()) {
      markSessionExpired();
      return;
    }
    setError(t('session.unlockFailed'));
  };

  const completeOidcUnlock = async (popup: Window | null, nonce: string) => {
    setOidcPending(true);
    const expected = captureUnlockIdentity();
    if (expected === null) {
      failOidcUnlock(popup);
      return;
    }

    let startUrl: string;
    try {
      startUrl = await startOidcUnlock();
    } catch (err) {
      failOidcUnlock(popup, err);
      return;
    }

    if (!isSafeUnlockStartUrl(startUrl)) {
      failOidcUnlock(popup);
      return;
    }

    if (!identityStillCurrent(expected)) {
      failOidcUnlock(popup);
      return;
    }

    const popupOpen = popup !== null && !popup.closed;
    if (!popupOpen) {
      try {
        popup?.close();
      } catch {
        // Blocked or already closed.
      }
      const returnTo = currentReturnPath();
      if (isSafeInternalPath(returnTo)) {
        useAuthStore.getState().setRedirectAfterLogin(returnTo);
      }
      window.location.assign(startUrl);
      return;
    }

    const { refreshToken, ...identity } = expected;
    popupCleanupRef.current?.();
    popupCleanupRef.current = subscribeUnlockPopup(
      popup,
      identity,
      {
        onSuccess: (tokens) => {
          popupCleanupRef.current = null;
          try {
            popup.close();
          } catch {
            // Opener still applies the tokens.
          }
          if (!identityStillCurrent(expected)) {
            setOidcPending(false);
            setError(t('session.unlockFailed'));
            return;
          }
          useAuthStore.getState().unlockSession(tokens.accessToken, tokens.refreshToken);
          setOidcPending(false);
          setError(null);
        },
        onFailure: () => {
          popupCleanupRef.current = null;
          try {
            popup.close();
          } catch {
            // Already closed by the user.
          }
          setOidcPending(false);
          setError(t('session.unlockFailed'));
        },
      },
      { nonce, refreshToken },
    );
    assignUnlockPopupLocation(popup, startUrl);
  };

  const handleOidcUnlock = () => {
    setError(null);
    const popup = openUnlockPopup();
    const nonce = popup !== null ? prepareUnlockPopup(popup) : '';
    void completeOidcUnlock(popup, nonce);
  };

  return (
    <Dialog
      open
      fullScreen
      disableEscapeKeyDown
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 16 }}
      slotProps={{
        paper: {
          sx: {
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <Stack spacing={2} sx={{ width: '100%', maxWidth: 420, px: 3, py: 4 }}>
        <DialogTitle
          id={titleId}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0 }}
        >
          <LockOutlinedIcon color="action" />
          <Typography variant="h5" component="span" fontWeight={600}>
            {t('session.lockTitle')}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 0 }}>
          <Typography id={descriptionId} variant="body1" sx={{ mb: 2 }}>
            {displayName === ''
              ? t('session.lockMessageAnonymous')
              : t('session.lockMessage', { name: displayName })}
          </Typography>

          {displayName !== '' && (
            <Typography variant="h6" component="p" sx={{ mb: 3 }}>
              {t('session.lockedAs', { name: displayName })}
            </Typography>
          )}

          {error !== null && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }} role="alert">
              {error}
            </Typography>
          )}

          {sessionExpired ? (
            <Typography variant="body2" sx={{ mb: 2 }} role="alert">
              {t('session.lockSessionExpired')}
            </Typography>
          ) : !contextReady && contextFailed && !contextFetching ? (
            <Stack spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
              <Typography variant="body2" color="error" role="alert">
                {t('session.lockContextFailed')}
              </Typography>
              <Button variant="text" onClick={() => void refetchAuthContext()}>
                {t('session.lockContextRetry')}
              </Button>
            </Stack>
          ) : !contextReady ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : payaIdEnabled ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('session.unlockOidcHint')}
            </Typography>
          ) : (
            <TextField
              autoFocus
              fullWidth
              type="password"
              label={t('session.unlockPassword')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handlePasswordUnlock();
                }
              }}
              autoComplete="current-password"
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 0, gap: 1, justifyContent: 'space-between' }}>
          <Button variant="outlined" color="inherit" onClick={() => void signOut()}>
            {t('logout')}
          </Button>
          {sessionExpired ? (
            <Button
              variant="contained"
              onClick={() => void handleSignInAgain()}
              disabled={signingIn}
            >
              {signingIn ? <CircularProgress size={24} /> : t('session.signInAgain')}
            </Button>
          ) : contextReady && payaIdEnabled ? (
            <Button
              variant="contained"
              onClick={handleOidcUnlock}
              disabled={oidcPending}
            >
              {oidcPending ? <CircularProgress size={24} /> : t('session.unlock')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => void handlePasswordUnlock()}
              disabled={!contextReady || unlock.isPending || password === ''}
            >
              {unlock.isPending ? <CircularProgress size={24} /> : t('session.unlock')}
            </Button>
          )}
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
